/**
 * Utility functions for displaying data in the UI
 */

/**
 * Sanitizes a value that might be null, undefined, or the string "null"
 * Returns an empty string for display purposes
 */
export function sanitizeDisplayValue(value: string | null | undefined): string {
  if (value === null || value === undefined || value === 'null' || value === 'undefined' || value.trim() === '') {
    return '';
  }
  return value;
}

/**
 * Known canonical enum values used in analysis panels.
 * Sorted by length descending so longer prefixes match first
 * (e.g., "definitely_preventable" before "definitely").
 */
const KNOWN_ENUMS: string[] = [
  // Preventability
  'definitely_preventable', 'probably_preventable', 'possibly_preventable', 'not_preventable',
  // Compliance
  'fully_compliant', 'substantially_compliant', 'partially_compliant', 'non_compliant', 'not_applicable',
  // Harm
  'severe_harm', 'moderate_harm', 'minor_harm', 'no_harm',
  // Event types
  'clinical_event',
  // Inconsistency
  'minor_inconsistencies', 'minor_deviations', 'significant_concerns', 'critical_issues', 'no_issues',
  // Strength / quality
  'definite', 'established', 'probable', 'possible', 'unlikely', 'excluded',
  'excellent', 'good', 'adequate', 'questionable', 'poor', 'inadequate',
  'thorough', 'limited', 'absent', 'unclear',
  // Contribution / severity
  'major', 'moderate', 'minor', 'critical', 'significant',
  'high', 'medium', 'low',
  'death',
  // Event types (single word)
  'action', 'omission', 'intervention',
  // Comparative
  'definitely', 'probably', 'possibly',
  // Standard of care
  'compliant', 'deviation', 'breach', 'explainable',
  // Fallback
  'info',
].sort((a, b) => b.length - a.length);

/**
 * Extracts a clean enum value from a potentially verbose LLM response.
 *
 * LLMs sometimes append rationale to enum fields, e.g.:
 *   "Definite — analytical observation based on documented evidence..."
 * This strips everything after common delimiters and matches against known enums.
 */
export function sanitizeEnumValue(raw: string | undefined | null): string {
  if (!raw) return '';

  // Strip rationale after common LLM delimiters
  const cleaned = raw
    .split(/\s*—\s*/)[0]   // em dash
    .split(/\s+-\s+/)[0]    // spaced hyphen
    .split(/:\s+/)[0]       // colon
    .trim();

  const normalized = cleaned.toLowerCase().replace(/\s+/g, '_');

  for (const key of KNOWN_ENUMS) {
    if (normalized === key || normalized.startsWith(key)) {
      return key;
    }
  }

  return normalized;
}

/** Sanitize an enum value and format for display (underscores → spaces). */
export function enumDisplay(raw: string | undefined | null): string {
  return sanitizeEnumValue(raw).replace(/_/g, ' ');
}
